package com.milta.feature.settings.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.profile.domain.usecase.GetProfileUseCase
import com.milta.feature.profile.domain.usecase.UpdateLanguageUseCase
import kotlinx.coroutines.launch

/**
 * ViewModel for the Admin dashboard's "Paramètres" screen (2026-10-05) — a trimmed-down sibling of
 * [WebSettingsViewModel]: deliberately does NOT inject [com.milta.feature.team.domain.usecase.GetMyEnterpriseUseCase]/
 * [com.milta.feature.subscription.domain.usecase.GetMySubscriptionUseCase]/
 * [com.milta.feature.team.domain.usecase.DeleteEnterpriseUseCase] — those would 403 immediately for
 * an Admin account, which has no Organisation (see [Screen.AdminDashboard]'s doc in webApp's Main.kt).
 */
class AdminSettingsViewModel(
    private val getProfile: GetProfileUseCase,
    private val updateLanguage: UpdateLanguageUseCase,
) : MviViewModel<AdminSettingsState, AdminSettingsIntent>(AdminSettingsState()) {

    init {
        onIntent(AdminSettingsIntent.Load)
    }

    override fun onIntent(intent: AdminSettingsIntent) {
        when (intent) {
            AdminSettingsIntent.Load -> load()
            is AdminSettingsIntent.SelectLanguage -> selectLanguage(intent.tag)
        }
    }

    private fun load() {
        scope.launch {
            setState { copy(isLoading = true) }
            getProfile().onSuccess { profile -> setState { copy(language = profile.language) } }
            setState { copy(isLoading = false) }
        }
    }

    private fun selectLanguage(tag: String) {
        if (currentState.isLanguageSaving) return
        // Guard set synchronously, before scope.launch — same double-tap protection as
        // WebSettingsViewModel.selectLanguage.
        setState { copy(isLanguageSaving = true, languageError = null) }
        scope.launch {
            updateLanguage(tag).fold(
                onSuccess = { setState { copy(isLanguageSaving = false, language = tag) } },
                onFailure = { e ->
                    val code = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isLanguageSaving = false, languageError = code) }
                },
            )
        }
    }
}
